/**
 * hooks/useProviderConnection.ts
 * Shared connection/disconnection logic for sync providers
 *
 * Purpose:
 * - Extract duplicated connection logic from DataSyncSettings
 * - Provide cross-tab coordination via SyncCoordinatorService
 * - Implement timeout protection and abort handling
 * - PII masking in all telemetry
 * - UI-agnostic design (returns toast data, doesn't show toasts)
 *
 * Requirements:
 * - userId must be non-empty string
 * - timeoutMs mirrors SyncInitializationService (default 10000ms)
 * - Never instantiates providers directly (uses providerFactory)
 * - Cleanup guaranteed on unmount
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// Type imports
import type { ProviderKey, SyncInitReason, ConnectionFailure } from '@/types/sync';
import type { SyncProvider, ProviderConnectOptions } from '@/types/sync/SyncProvider';
import type { ToastShape } from '@/utils';

// Utility imports
import {
  buildSyncErrorToastShape,
  getSyncErrorMessage,
  logReconnectionEvent,
  maskUserId,
} from '@/utils';
import { generateUUID } from '@/utils/uuid';
import { isOnline } from '@/utils/connectionUtils';

// Services
import { syncCoordinatorService } from '@/services/sync/SyncCoordinatorService';
import { createProvider } from '@/services/sync/providerFactory';
import { userSyncSettingsService } from '@/services/UserSyncSettingsService';
import { BackgroundSyncService } from '@/services/sync/BackgroundSyncService';
import { syncInitializationService } from '@/services/sync/SyncInitializationService';

// Internal helpers
import { withTimeout } from './withTimeout';
import { mapErrorToReason } from './mapErrorToReason';

/**
 * OAuth safety cap timeout (5 minutes).
 * Used for OAuth flows where user interaction time is unpredictable.
 * This is a safety net for truly hung popups, not an expected timeout.
 */
const OAUTH_SAFETY_CAP_MS = 5 * 60 * 1000; // 5 minutes

// Note: We intentionally don't poll for popup closure because:
// 1. Popup closes naturally after OAuth completes (redirect back)
// 2. We can't distinguish "user cancelled" from "OAuth succeeded"
// 3. The 5-minute safety cap handles truly hung flows
// 4. If user cancels, connect() fails naturally (no OAuth callback)

/**
 * UI feedback signal returned by the hook.
 * Hook is UI-agnostic: it returns toast data but doesn't show toasts.
 * Components using this hook are responsible for rendering toasts.
 *
 * @example
 * ```tsx
 * const { lastUiSignal } = useProviderConnection({ userId });
 *
 * useEffect(() => {
 *   if (lastUiSignal) {
 *     showToast(toastShapeToOptions(lastUiSignal.toast));
 *   }
 * }, [lastUiSignal]);
 * ```
 */
export interface UiSignal {
  /** Severity level for UI feedback (info/warning/error) */
  severity: 'info' | 'warning' | 'error';
  /** Toast shape data (title, description, color, timeout) */
  toast: ToastShape;
}

/**
 * Discriminated union representing connection state.
 *
 * States:
 * - idle: No active connection or connection attempt
 * - connecting: Connection attempt in progress (local tab initiated)
 * - remoteConnecting: Another tab is attempting connection (passive/waiting state)
 * - success: Successfully connected, providerKey stored (NOT instance)
 * - needsReauth: Requires user re-authentication (OAuth expired/revoked)
 * - offline: User is offline, cannot attempt connection
 * - rateLimited: Provider API rate limit exceeded (NETWORK PROVIDERS ONLY)
 * - error: Connection failed with non-recoverable error
 *
 * IMPORTANT: providerKey is stored, NOT provider instance (avoids serialization issues)
 */
export type ConnectionState =
  | { status: 'idle' }
  | { status: 'connecting' }
  | { status: 'remoteConnecting'; providerKey: ProviderKey }
  | { status: 'success'; providerKey: ProviderKey }
  | { status: 'needsReauth'; reason: SyncInitReason; error: string }
  | { status: 'offline' }
  | { status: 'rateLimited'; retryAfterMs: number }
  | { status: 'error'; reason: SyncInitReason; error: string };

/**
 * Options for useProviderConnection hook.
 *
 * Required:
 * - userId: Non-empty user ID for telemetry and settings updates
 *
 * Optional:
 * - timeoutMs: Connection timeout in ms (default: 10000, mirrors SyncInitializationService)
 */
export interface UseProviderConnectionOptions {
  /** User ID for telemetry and settings updates. Must be non-empty. */
  userId: string;
  /**
   * Timeout for connection attempts in milliseconds.
   * Default: 10000ms (mirrors SyncInitializationService)
   */
  timeoutMs?: number;
}

/**
 * Return type for useProviderConnection hook.
 *
 * Functions:
 * - connect: Initiate connection with timeout protection and cross-tab coordination
 * - disconnect: Disconnect provider and clean up (graceful, no throw)
 * - abort: Cancel in-flight connection attempt
 * - hydrateFromInitResult: Set state from SyncInitializationService failure (for cold-start)
 * - hydrateSuccess: Set state to success from SyncInitializationService success (for cold-start)
 *
 * State:
 * - state: Current connection state (discriminated union)
 * - lastUiSignal: Last UI signal generated (for toast rendering, null if none)
 */
export interface UseProviderConnectionReturn {
  /** Current connection state */
  state: ConnectionState;
  /**
   * Last UI signal generated (for toast rendering).
   * Null if no signal yet.
   * Components should watch this and render toasts accordingly.
   */
  lastUiSignal: UiSignal | null;
  /** Initiate connection with timeout protection and cross-tab coordination. Returns true on success, false on failure. */
  connect: (provider: ProviderKey, options?: ProviderConnectOptions) => Promise<boolean>;
  /** Disconnect provider and clean up (graceful, no throw) */
  disconnect: () => Promise<void>;
  /** Cancel in-flight connection attempt */
  abort: () => void;
  /**
   * Hydrate connection state from SyncInitializationService failure result.
   * Called on cold-start when init detects an error (TOKEN_EXPIRED, OFFLINE, etc.)
   * to ensure the shared state reflects the initialization outcome.
   *
   * This is the "single source of truth" pattern - instead of components opening
   * modals/banners directly, they hydrate state and let existing effects react.
   *
   * @param failure - ConnectionFailure from SyncInitializationService
   */
  hydrateFromInitResult: (failure: ConnectionFailure) => void;
  /**
   * Hydrate connection state to success after SyncInitializationService succeeds.
   * Called on cold-start when init successfully connects to update shared state.
   *
   * This mirrors hydrateFromInitResult but for the success path, ensuring
   * SyncConnectionContext reflects the initialization outcome.
   *
   * @param providerKey - The provider that was successfully connected
   */
  hydrateSuccess: (providerKey: ProviderKey) => void;
}

/**
 * Shared connection/disconnection logic for sync providers.
 *
 * Features:
 * - Discriminated union state (idle | connecting | success | needsReauth | offline | rateLimited | error)
 * - Timeout protection (10s default, configurable)
 * - Cross-tab coordination via SyncCoordinatorService (BroadcastChannel)
 * - PII masking in telemetry (userId, email, folderId)
 * - Abort support for cancelling in-flight requests
 * - UI-agnostic: returns toast data via lastUiSignal, doesn't show toasts
 *
 * Requirements:
 * - userId must be non-empty string
 * - Components must render toasts based on lastUiSignal
 * - Never instantiates providers directly (uses providerFactory)
 * - Cleanup guaranteed on unmount (abort, timers, listeners)
 *
 * @param options - Configuration options (userId required, timeoutMs optional)
 * @returns Connection state, UI signals, and control functions
 *
 * @example Basic usage
 * ```tsx
 * const { state, lastUiSignal, connect, disconnect, abort } = useProviderConnection({
 *   userId: user.id,
 *   timeoutMs: 10000
 * });
 *
 * // Watch for UI signals and render toasts
 * useEffect(() => {
 *   if (lastUiSignal) {
 *     showToast(toastShapeToOptions(lastUiSignal.toast));
 *   }
 * }, [lastUiSignal]);
 *
 * // Connect to provider
 * await connect('google-drive');
 *
 * // Check state
 * if (state.status === 'success') {
 *   console.log('Connected to:', state.providerKey);
 * }
 * ```
 */
export function useProviderConnection(
  options: UseProviderConnectionOptions
): UseProviderConnectionReturn {
  // ===== VALIDATION =====

  // Validate userId (must be non-empty)
  if (!options.userId || options.userId.trim() === '') {
    throw new Error(
      'useProviderConnection: userId is required and must be non-empty'
    );
  }

  // ===== DEFAULTS =====

  // Apply timeout default (mirrors SyncInitializationService)
  const timeoutMs = options.timeoutMs ?? 10_000;

  // ===== STATE =====

  // DEV MODE: Check for error state injection via query params (E2E testing support)
  const getInitialState = (): ConnectionState => {
    if (import.meta.env.DEV && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const errorState = params.get('errorState');
      const oauthSuccess = params.get('oauth_success');

      console.log('[useProviderConnection] getInitialState called', {
        url: window.location.href,
        errorState,
        oauthSuccess,
        hasQueryParams: params.toString(),
      });

      // Guard: Don't re-apply error state after successful OAuth callback
      // This prevents infinite loop where oauth_success navigation re-triggers needsReauth
      // The existing modal instance will complete its flow to success → auto-close
      if (oauthSuccess === 'true' && errorState) {
        console.log('[useProviderConnection] Skipping errorState injection (OAuth callback in progress)');
        return { status: 'idle' };
      }

      if (errorState === 'offline') {
        console.log('[useProviderConnection] Returning offline state');
        return { status: 'offline' };
      }
      if (errorState === 'rateLimited') {
        const retryAfter = parseInt(params.get('retryAfter') || '60', 10);
        return { status: 'rateLimited', retryAfterMs: retryAfter * 1000 };
      }
      if (errorState === 'needsReauth') {
        const reason = (params.get('reason') || 'TOKEN_EXPIRED') as SyncInitReason;
        return { status: 'needsReauth', reason, error: 'Simulated auth error (dev mode)' };
      }
      if (errorState === 'error') {
        const reason = (params.get('reason') || 'TIMEOUT') as SyncInitReason;
        return { status: 'error', reason, error: 'Simulated connection error (dev mode)' };
      }
    }
    console.log('[useProviderConnection] Returning idle state');
    return { status: 'idle' };
  };

  // Connection state (discriminated union)
  const [state, setState] = useState<ConnectionState>(getInitialState);

  // UI signal state (for toast rendering)
  const [lastUiSignal, setLastUiSignal] = useState<UiSignal | null>(null);

  // ===== REFS FOR CLEANUP =====

  // Provider instance (NOT in state to avoid serialization issues)
  const providerRef = useRef<SyncProvider | null>(null);

  // AbortController for timeout/cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Timeout ID for cleanup
  const timeoutIdRef = useRef<number | null>(null);

  // Correlation ID for cross-tab tracking (active reconnection)
  const correlationIdRef = useRef<string | null>(null);

  // Unsubscribe function for BroadcastChannel cleanup
  const unsubscribeRef = useRef<(() => void) | null>(null);

  // Previous actionable state for failure recovery when remote tab fails
  // Stores the state before entering 'remoteConnecting' so we can restore it
  const previousActionableStateRef = useRef<ConnectionState | null>(null);

  // ===== CROSS-TAB COORDINATION =====

  /**
   * Cross-tab message handler for reconnection coordination.
   * Filters for 'reconnect:start' and 'reconnect:end' messages.
   * Ignores own messages (same originId).
   *
   * Flow:
   * - reconnect:start: Store previous state, transition to remoteConnecting
   * - reconnect:end (success): Transition to success state
   * - reconnect:end (failure): Restore previous actionable state
   */
  const handleReconnectionMessage = useCallback(
    (message: {
      type: string;
      provider?: ProviderKey;
      correlationId?: string;
      success?: boolean;
      reason?: SyncInitReason;
      originId: string;
    }): void => {
      // Ignore own messages (self-bounce guard)
      if (message.originId === syncCoordinatorService['originId']) {
        return;
      }

      if (message.type === 'reconnect:start' && message.provider) {
        // Another tab started reconnection for same provider
        if (message.correlationId && correlationIdRef.current === null) {
          // Track correlation ID
          correlationIdRef.current = message.correlationId;

          // Capture provider key for use in callback (avoids non-null assertion)
          const remoteProviderKey = message.provider;

          // Store current state for failure recovery (before transitioning)
          // Use callback form to capture latest state
          setState((currentState) => {
            // Only store if not already in a transient state
            if (
              currentState.status !== 'connecting' &&
              currentState.status !== 'remoteConnecting' &&
              currentState.status !== 'success'
            ) {
              previousActionableStateRef.current = currentState;
            }
            // Transition to remoteConnecting
            return { status: 'remoteConnecting', providerKey: remoteProviderKey };
          });

          // Set UI signal for toast (components may still want this)
          setLastUiSignal({
            severity: 'info',
            toast: {
              title: 'Reconnecting',
              description: 'Another tab is reconnecting...',
              color: 'default',
              timeout: 5000,
            },
          });
        }
      } else if (message.type === 'reconnect:end') {
        // Another tab completed reconnection
        if (message.correlationId === correlationIdRef.current) {
          // Clear tracked correlation ID
          correlationIdRef.current = null;

          if (message.success && message.provider) {
            // Clear previous state ref (no longer needed)
            previousActionableStateRef.current = null;

            // Transition to success state
            setState({ status: 'success', providerKey: message.provider });
            setLastUiSignal({
              severity: 'info',
              toast: {
                title: 'Connected',
                description: 'Connection restored by another tab.',
                color: 'success',
                timeout: 5000,
              },
            });
          } else {
            // Other tab failed - restore previous actionable state
            const previousState = previousActionableStateRef.current;
            previousActionableStateRef.current = null;

            if (previousState) {
              // Restore the state we had before entering remoteConnecting
              setState(previousState);
            } else if (message.reason) {
              // Fallback: no previous state, use generic error
              setState({
                status: 'error',
                reason: message.reason,
                error: 'Failed in another tab',
              });
            } else {
              // Ultimate fallback: return to idle
              setState({ status: 'idle' });
            }
          }
        }
      }
    },
    []
  );

  // ===== MAIN FUNCTIONS =====

  /**
   * Initiate connection with timeout protection and cross-tab coordination.
   *
   * Flow:
   * 1. Check offline status (early exit)
   * 2. Generate correlation ID and broadcast 'reconnect:start'
   * 3. Create provider via factory (never direct instantiation)
   * 4. Wrap provider.connect() with timeout
   * 5. Confirm connection with provider.isConnected()
   * 6. Update settings and start BackgroundSyncService
   * 7. Broadcast 'reconnect:end' with result
   * 8. Log telemetry with PII masking
   *
   * @param provider - Provider key ('google-drive' | 'local')
   * @param connectOptions - Optional connection options (e.g., promptMode for Google Drive)
   * @returns Promise<boolean> - true on success, false on failure/cancellation
   */
  const connect = useCallback(
    async (provider: ProviderKey, connectOptions?: ProviderConnectOptions): Promise<boolean> => {
      // Check if already connecting (local or remote)
      if (state.status === 'connecting' || state.status === 'remoteConnecting') {
        console.warn(
          '[useProviderConnection] Connection already in progress (local or remote), ignoring'
        );
        return false;
      }

      // Check offline first (early exit) - ONLY FOR CLOUD PROVIDERS
      // Local Directory provider uses File System API which works offline
      if (!isOnline() && provider !== 'local') {
        setState({ status: 'offline' });
        logReconnectionEvent({
          event: 'fail',
          provider,
          reason: 'OFFLINE',
          userId: maskUserId(options.userId),
          correlationId: 'offline-early-exit',
        });
        setLastUiSignal({
          severity: 'warning',
          toast: {
            title: 'Offline',
            description: 'You are offline. Connect to the internet to sync.',
            color: 'warning',
            timeout: 5000,
          },
        });
        return false;
      }

      // Generate correlation ID for cross-tab tracking
      const correlationId = generateUUID();
      correlationIdRef.current = correlationId;

      // Create AbortController for timeout/cancellation
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      // Broadcast reconnect:start
      syncCoordinatorService['broadcast']({
        type: 'reconnect:start',
        provider,
        correlationId,
        originId: syncCoordinatorService['originId'],
        ts: Date.now(),
        version: 'v1',
      });

      // Set state to connecting
      setState({ status: 'connecting' });

      // Log telemetry (clicked event)
      const startTime = Date.now();
      logReconnectionEvent({
        event: 'clicked',
        provider,
        reason: 'SUCCESS',
        userId: maskUserId(options.userId),
        correlationId,
      });

      try {
        // Create provider via factory (NEVER use 'new Provider()')
        const providerInstance = createProvider(provider);
        if (!providerInstance) {
          throw new Error(`Failed to create provider: ${provider}`);
        }
        providerRef.current = providerInstance;

        // Connect with appropriate timeout strategy based on user-interaction requirements
        // User-interaction flows (OAuth popup, FSA picker): Use generous safety cap (5 min)
        // Machine-only flows (future providers): Use standard timeout (10s)
        if (connectOptions?.oauthPopup || provider === 'local') {
          // User-interaction path: Use generous safety cap (5 min) instead of strict 10s timeout
          // - OAuth: User needs time to load Google page, read permissions, click Allow, redirect
          // - Local FSA: User needs time to navigate folders and select one in the picker
          await withTimeout(
            providerInstance.connect(connectOptions),
            OAUTH_SAFETY_CAP_MS,
            abortController.signal
          );
        } else {
          // Machine-only path (no user interaction required): Use standard timeout
          await withTimeout(
            providerInstance.connect(connectOptions),
            timeoutMs,
            abortController.signal
          );
        }

        // Confirm connection (machine-only step, keep standard timeout)
        const connected = await withTimeout(
          providerInstance.isConnected(),
          timeoutMs,
          abortController.signal
        );

        console.log(`[useProviderConnection] isConnected() returned: ${connected}`);

        if (connected) {
          // Update settings (atomic update)
          // Enable syncOnLogin so future logins auto-sync with the newly connected provider
          await userSyncSettingsService.updateSettings(options.userId, {
            syncEnabled: true,
            activeSyncProvider: provider,
            syncOnLogin: true,
          });

          // Initialize sync engine via SyncInitializationService (single owner pattern)
          await syncInitializationService.initializeAfterOnboarding(options.userId, provider);

          // Set state to success
          console.log(`[useProviderConnection] Setting state to success for ${provider}`);
          setState({ status: 'success', providerKey: provider });

          // Set UI signal
          const providerName =
            provider === 'google-drive' ? 'Google Drive' : 'Local Directory';
          setLastUiSignal({
            severity: 'info',
            toast: {
              title: 'Connected',
              description: `Successfully connected to ${providerName}.`,
              color: 'success',
              timeout: 5000,
            },
          });

          // Broadcast reconnect:end (success)
          syncCoordinatorService['broadcast']({
            type: 'reconnect:end',
            provider,
            correlationId,
            success: true,
            reason: 'SUCCESS',
            originId: syncCoordinatorService['originId'],
            ts: Date.now(),
            version: 'v1',
          });

          // Log telemetry (success)
          logReconnectionEvent({
            event: 'success',
            provider,
            reason: 'SUCCESS',
            userId: maskUserId(options.userId),
            correlationId,
            durationMs: Date.now() - startTime,
          });

          console.log(`[useProviderConnection] Returning true from connect()`);
          return true;
        } else {
          // Connected but isConnected() returned false (treat as token expired)
          console.log(`[useProviderConnection] isConnected() was false, throwing TOKEN_EXPIRED`);
          throw new Error('TOKEN_EXPIRED: Connection not confirmed');
        }
      } catch (error) {
        console.log(`[useProviderConnection] Caught error in connect():`, error);
        // Map error to SyncInitReason
        const { reason, retryAfterMs } = mapErrorToReason(error);

        // Handle ABORTED silently (user clicked Cancel button) - return to idle
        if (reason === 'ABORTED') {
          setState({ status: 'idle' });
          correlationIdRef.current = null;
          providerRef.current = null; // Clear stale provider instance
          return false;
        }

        // Handle USER_CANCELLED (popup closed) - show cancelled message in modal
        // Token is still expired, so we show error state with cancellation message
        if (reason === 'USER_CANCELLED') {
          setState({
            status: 'error',
            reason: 'USER_CANCELLED',
            error: 'Connection cancelled',
          });
          correlationIdRef.current = null;
          providerRef.current = null;
          return false;
        }

        // Determine state based on reason
        let newState: ConnectionState;
        let severity: 'info' | 'warning' | 'error' = 'error';

        // Rate limiting (only for network providers like Google Drive)
        if (reason === 'PROVIDER_OUTAGE' && retryAfterMs && provider === 'google-drive') {
          newState = { status: 'rateLimited', retryAfterMs };
          severity = 'warning';
        }
        // Needs reauth (recoverable)
        else if (
          reason === 'TOKEN_EXPIRED' ||
          reason === 'TOKEN_REVOKED' ||
          reason === 'PERMISSION_DENIED' ||
          reason === 'DIRECTORY_INVALID'
        ) {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          newState = { status: 'needsReauth', reason, error: errorMessage };
          severity = 'error';
        }
        // Offline
        else if (reason === 'OFFLINE') {
          newState = { status: 'offline' };
          severity = 'warning';
        }
        // Other errors
        else {
          const errorMessage =
            error instanceof Error ? error.message : String(error);
          newState = { status: 'error', reason, error: errorMessage };
          severity = 'error';
        }

        setState(newState);

        // Build error message using utility functions
        const providerType = provider === 'google-drive' ? 'google-drive' : 'local';
        const providerDisplayName =
          provider === 'google-drive' ? 'Google Drive' : 'Local Directory';

        const errorMsg = getSyncErrorMessage(
          {
            code: 'NOT_CONNECTED',
            message: error instanceof Error ? error.message : String(error),
            provider: providerType,
          },
          'toast',
          providerDisplayName
        );

        // Set UI signal
        setLastUiSignal({
          severity,
          toast: buildSyncErrorToastShape({
            code: 'NOT_CONNECTED',
            message: errorMsg.description,
            provider: providerType,
          }),
        });

        // Broadcast reconnect:end (failure)
        const broadcastPayload: {
          type: 'reconnect:end';
          provider: ProviderKey;
          correlationId: string;
          success: boolean;
          reason: SyncInitReason;
          retryAfterMs?: number;
          originId: string;
          ts: number;
          version: 'v1';
        } = {
          type: 'reconnect:end',
          provider,
          correlationId,
          success: false,
          reason,
          originId: syncCoordinatorService['originId'],
          ts: Date.now(),
          version: 'v1',
        };

        if (retryAfterMs !== undefined) {
          broadcastPayload.retryAfterMs = retryAfterMs;
        }

        syncCoordinatorService['broadcast'](broadcastPayload);

        // Log telemetry (fail)
        const telemetryPayload: {
          event: 'fail';
          provider: ProviderKey;
          reason: SyncInitReason;
          userId: string;
          correlationId: string;
          durationMs: number;
          retryAfterMs?: number;
          details: string;
        } = {
          event: 'fail',
          provider,
          reason,
          userId: maskUserId(options.userId),
          correlationId,
          durationMs: Date.now() - startTime,
          details:
            error instanceof Error
              ? error.message.substring(0, 100)
              : String(error).substring(0, 100),
        };

        if (retryAfterMs !== undefined) {
          telemetryPayload.retryAfterMs = retryAfterMs;
        }

        logReconnectionEvent(telemetryPayload);

        // Clear stale provider instance on failure
        providerRef.current = null;

        return false;
      } finally {
        // Cleanup
        abortControllerRef.current = null;
        correlationIdRef.current = null;
      }
    },
    [state.status, options.userId, timeoutMs]
  );

  /**
   * Disconnect provider and clean up.
   * Graceful operation - does not throw on errors.
   */
  const disconnect = useCallback(async (): Promise<void> => {
    // Check if already idle
    if (state.status === 'idle') {
      console.warn('[useProviderConnection] Already disconnected, ignoring');
      return;
    }

    try {
      // Get provider from ref
      const provider = providerRef.current;

      // Disconnect provider if exists
      if (provider) {
        await provider.disconnect();
      }

      // Stop BackgroundSyncService
      const syncService = BackgroundSyncService.getInstance();
      await syncService.stop();

      // Update settings (preserve activeSyncProvider for "previously configured" state)
      await userSyncSettingsService.updateSettings(options.userId, {
        syncEnabled: false,
      });

      // Clear provider ref
      providerRef.current = null;

      // Set state to idle
      setState({ status: 'idle' });

      // Set UI signal
      setLastUiSignal({
        severity: 'info',
        toast: {
          title: 'Disconnected',
          description: 'Successfully disconnected from sync provider.',
          color: 'default',
          timeout: 5000,
        },
      });
    } catch (error) {
      // Log error but don't throw (graceful disconnect)
      console.error(
        '[useProviderConnection] Disconnect error (non-fatal):',
        error instanceof Error ? error.message : String(error)
      );

      // Still set state to idle (best effort)
      setState({ status: 'idle' });
      providerRef.current = null;
    }
  }, [state.status, options.userId]);

  /**
   * Aborts in-flight connection attempt.
   * Triggers ABORTED error in connect catch block.
   */
  const abort = useCallback((): void => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // ===== CLEANUP HELPERS =====

  /**
   * Clear any pending timeout.
   * Reads latest ref value at call time (not at definition).
   */
  const clearAnyPendingTimeout = useCallback((): void => {
    const id = timeoutIdRef.current;
    if (id !== null) {
      clearTimeout(id);
      timeoutIdRef.current = null;
    }
  }, []);

  /**
   * Abort in-flight operations.
   * Reads latest ref value at call time.
   */
  const abortInFlight = useCallback((): void => {
    const ac = abortControllerRef.current;
    if (ac) {
      ac.abort();
      abortControllerRef.current = null;
    }
  }, []);

  /**
   * Unsubscribe from cross-tab coordinator.
   * Reads latest ref value at call time.
   */
  const unsubscribeFromCoordinator = useCallback((): void => {
    const un = unsubscribeRef.current;
    if (un) {
      try {
        un();
      } finally {
        unsubscribeRef.current = null;
      }
    }
  }, []);

  /**
   * Clear provider ref.
   */
  const clearProviderRef = useCallback((): void => {
    providerRef.current = null;
  }, []);

  // ===== HYDRATION FROM INIT RESULT =====

  /**
   * Hydrate connection state from SyncInitializationService failure result.
   *
   * Maps ConnectionFailure to ConnectionState:
   * - TOKEN_EXPIRED, TOKEN_REVOKED, PERMISSION_DENIED, DIRECTORY_INVALID → needsReauth
   * - OFFLINE → offline
   * - RATE_LIMITED → rateLimited
   * - Other failures → error
   *
   * This ensures the shared state reflects initialization outcomes so that
   * ReconnectionModal/Banner render the correct content (single source of truth).
   */
  const hydrateFromInitResult = useCallback((failure: ConnectionFailure): void => {
    const { reason, error, retryAfterMs } = failure;

    console.log('[useProviderConnection] hydrateFromInitResult called:', { reason, error });

    // Map reason to appropriate ConnectionState
    switch (reason) {
      // Needs reauth (modal-required errors)
      case 'TOKEN_EXPIRED':
      case 'TOKEN_REVOKED':
      case 'PERMISSION_DENIED':
      case 'DIRECTORY_INVALID':
        setState({
          status: 'needsReauth',
          reason,
          error: error || `Connection requires re-authentication (${reason})`,
        });
        break;

      // Offline
      case 'OFFLINE':
        setState({ status: 'offline' });
        break;

      // Rate limited
      case 'RATE_LIMITED':
        setState({
          status: 'rateLimited',
          retryAfterMs: retryAfterMs || 60_000, // Default 60s if not specified
        });
        break;

      // Other errors (timeout, provider outage, unknown)
      case 'TIMEOUT':
      case 'PROVIDER_OUTAGE':
      case 'UNKNOWN_ERROR':
        setState({
          status: 'error',
          reason,
          error: error || `Connection failed (${reason})`,
        });
        break;

      // User-initiated cancellations - return to idle (no error state)
      case 'USER_CANCELLED':
      case 'ABORTED':
        setState({ status: 'idle' });
        break;

      // Success should never be passed to this function
      case 'SUCCESS':
        console.warn('[useProviderConnection] hydrateFromInitResult called with SUCCESS - ignoring');
        break;

      default:
        // Fallback for any unhandled reasons
        setState({
          status: 'error',
          reason: reason as SyncInitReason,
          error: error || `Connection failed (${reason})`,
        });
    }
  }, []);

  /**
   * Hydrate connection state to success after cold-start init succeeds.
   * Called by PrivateAppProvider when initializeOnLogin() returns success.
   *
   * This is the success-path counterpart to hydrateFromInitResult.
   */
  const hydrateSuccess = useCallback((providerKey: ProviderKey): void => {
    console.log('[useProviderConnection] hydrateSuccess called:', { providerKey });
    setState({ status: 'success', providerKey });
  }, []);

  // ===== EFFECTS =====

  /**
   * Effect: Subscribe to cross-tab reconnection messages.
   * Cleanup guaranteed on unmount.
   * Strict Mode resilient: cleanup runs twice in dev, safe to call multiple times.
   */
  useEffect(() => {
    const unsubscribe = syncCoordinatorService['onReconnection'](
      handleReconnectionMessage
    );
    unsubscribeRef.current = unsubscribe;

    return () => {
      unsubscribeRef.current?.();
    };
  }, [handleReconnectionMessage]);

  /**
   * Effect: Cleanup on unmount.
   * Strict Mode safe: all cleanup operations are idempotent.
   */
  useEffect(() => {
    return () => {
      abortInFlight();
      clearAnyPendingTimeout();
      unsubscribeFromCoordinator();
      clearProviderRef();
    };
  }, [abortInFlight, clearAnyPendingTimeout, unsubscribeFromCoordinator, clearProviderRef]);

  // ===== RETURN =====

  return {
    state,
    lastUiSignal,
    connect,
    disconnect,
    abort,
    hydrateFromInitResult,
    hydrateSuccess,
  };
}
